import { CsvSchemaResponse } from '@/api/gen/model';
import {
  Paper,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  CircularProgress,
  Box,
  Typography,
  Alert,
  Chip,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import React from 'react';

interface CsvRecord {
  id: number;
  csvSchemaId: number;
  recordTimestampUtc: string;
  values: Record<string, any>;
}

interface PaginationInfo {
  totalCount: number;
  page: number;
  pageSize: number;
}

export default function CsvSchemaTable({
  csvSchemaHeader,
  data,
  isLoading,
  pagination,
  onPageChange,
  onPageSizeChange,
}: {
  csvSchemaHeader: CsvSchemaResponse;
  data: CsvRecord[];
  isLoading?: boolean;
  pagination?: PaginationInfo;
  onPageChange?: (newPage: number) => void;
  onPageSizeChange?: (newPageSize: number) => void;
}) {
  const formatValue = (value: any, dataType?: string) => {
    if (value === null || value === undefined) return 'N/A';

    if (dataType === 'DateTime') {
      return new Date(value).toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    if (dataType === 'Double') {
      return Number(value).toFixed(2);
    }

    if (dataType === 'Bool') {
      return value ? 'Sí' : 'No';
    }

    return String(value);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        minHeight: '600px',
        display: 'flex',
        flexDirection: 'column',
      }}
      id='csv-table-container'
    >
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='h6'>
          Registros {!isLoading && `(${data.length})`}
        </Typography>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            minHeight: '400px',
          }}
        >
          <CircularProgress />
        </Box>
      ) : data.length === 0 ? (
        <Alert severity='info'>No hay registros disponibles</Alert>
      ) : (
        <Box sx={{ overflowX: 'auto', flex: 1 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  ID
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  Timestamp
                </TableCell>
                {csvSchemaHeader.columns?.map(column => (
                  <TableCell
                    key={column.id}
                    sx={{ color: 'white', fontWeight: 'bold' }}
                  >
                    {column.header}
                    {column.isPrimary && (
                      <Chip
                        label='PK'
                        size='small'
                        sx={{ ml: 1, bgcolor: 'warning.main' }}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map(record => (
                <TableRow
                  key={record.id}
                  sx={{
                    '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                >
                  <TableCell>
                    <Chip label={record.id} size='small' color='primary' />
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2' sx={{ fontSize: '0.875rem' }}>
                      {formatTimestamp(record.recordTimestampUtc)}
                    </Typography>
                  </TableCell>
                  {csvSchemaHeader.columns?.map(column => (
                    <TableCell key={column.id}>
                      {formatValue(
                        record.values[column.header!],
                        column.dataType
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Paginación */}
      {pagination && onPageChange && onPageSizeChange && !isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              Mostrando {data.length} de {pagination.totalCount} registros
            </Typography>

            <FormControl size='small' sx={{ minWidth: 120 }}>
              <InputLabel>Por página</InputLabel>
              <Select
                value={pagination.pageSize}
                label='Por página'
                onChange={e => onPageSizeChange(Number(e.target.value))}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TablePagination
            component='div'
            count={pagination.totalCount}
            page={pagination.page - 1} // TablePagination usa 0-based, nosotros 1-based
            onPageChange={(event, newPage) => {
              event.preventDefault();
              onPageChange(newPage + 1);
            }}
            rowsPerPage={pagination.pageSize}
            onRowsPerPageChange={e => {
              e.preventDefault();
              onPageSizeChange(Number(e.target.value));
            }}
            labelRowsPerPage='Filas por página:'
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            sx={{ flexShrink: 0 }}
          />
        </Box>
      )}
    </Paper>
  );
}
